/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from "@ember/object";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import dayjs from "dayjs";
import { toLeft, toRight } from "ember-animated/transitions/move-over";
import type { IntlService } from "ember-intl";
import type { ForcBaseArgs } from "parog-web/components/forc/abstract/base-component";
import type {
  Question,
  Section
} from "parog-web/models/ctcae-filled-journal-event";
import CtcaeFilledJournalEvent from "parog-web/models/ctcae-filled-journal-event";
import { sleep } from "parog-web/utils/time";
import emberSwal from "ui-kit/utils/ember-swal";
import CtcaeBaseForm from "../base-form";

export default class CtcaeEditionForm extends CtcaeBaseForm {
  static fullComponentId = "ctcae/edition-form";
  static journalEventClass = CtcaeFilledJournalEvent;

  @tracked currentSectionIndex = 0;
  @tracked currentQuestionIndex = 0;
  @tracked currentQuestionTotalIndex = 0;

  @service declare intl: IntlService;

  duration = 300;

  rules({ oldItems, newItems }) {
    if (oldItems[0] < newItems[0]) {
      return toLeft;
    }
    return toRight;
  }

  constructor(owner: unknown, args: ForcBaseArgs) {
    super(owner, args);
    if (this.ctcaeFilledInTheLast24Hours) {
      emberSwal
        .fire({
          text: `Vous avez déjà rempli un questionnaire le ${this.intl.formatDate(
            this.ctcaeFilledInTheLast24Hours.occurredAt,
            { format: "DD/MM/YYYY" }
          )}. Voulez-vous appliquer les mêmes réponses ?`,
          icon: "question",
          cancelButtonText: "Annuler",
          showCancelButton: true,
          confirmButtonText: "Appliquer les réponses",
          confirmButtonColor: "#d33"
        })
        .then(result => {
          if (result.isConfirmed) {
            const sectionsToCopy = this.ctcaeFilledInTheLast24Hours
              ?.sectionsWithAnswers as Section[];
            this.sections.forEach(section => {
              section.questions.forEach(question => {
                const sectionToCopy = sectionsToCopy.find(
                  _section => _section.id == section.id
                );
                if (sectionToCopy) {
                  const questionToCopy = sectionToCopy.questions.find(
                    _question => _question.type == question.type
                  );
                  if (questionToCopy) {
                    question.answer = questionToCopy.answer;
                  }
                }
              });
            });
          }
        });
    }
  }

  get ctcaeFilledInTheLast24Hours(): CtcaeFilledJournalEvent | undefined {
    const lastCtcaeFilledJournalEvent =
      this.args.patient.lastCtcaeFilledJournalEvent;
    if (
      lastCtcaeFilledJournalEvent &&
      lastCtcaeFilledJournalEvent.occurredAt >
        dayjs().subtract(2, "d").startOf("day").toDate()
    ) {
      return lastCtcaeFilledJournalEvent;
    }
    return undefined;
  }

  get currentSection(): Section {
    return this.sections[this.currentSectionIndex];
  }

  get currentQuestion(): Question {
    return this.currentSection?.questions[this.currentQuestionIndex];
  }

  get totalNumberQuestions(): number {
    return this.sections.reduce((acc: number, obj: Section) => {
      return acc + obj.questions.length;
    }, 0);
  }

  get currentAnswers(): Array<Record<string, string>> {
    return CtcaeFilledJournalEvent.typeToValue(this.currentQuestion?.type);
  }

  get isLastQuestion(): boolean {
    return (
      this.currentSectionIndex == this.sections.length - 1 &&
      this.currentQuestionIndex == this.currentSection.questions.length - 1
    );
  }

  @action
  async previousQuestion() {
    this.currentQuestionTotalIndex--;
    await sleep(this.duration / 2);
    if (this.currentQuestionIndex > 0) {
      this.currentQuestionIndex--;
    } else if (this.currentSectionIndex > 0) {
      this.currentSectionIndex--;
      this.currentQuestionIndex = this.currentSection.questions.length - 1;
    }
  }

  @action
  async nextQuestion() {
    const questions = this.currentSection.questions;
    this.currentQuestionTotalIndex++;
    await sleep(this.duration / 2);
    if (this.currentQuestionIndex < questions.length - 1) {
      this.currentQuestionIndex++;
    } else if (this.currentSectionIndex < this.sections.length - 1) {
      this.currentSectionIndex++;
      this.currentQuestionIndex = 0;
    }
  }

  @action
  async onChange(value: string) {
    this.currentQuestion.answer = value;
    if (this.isLastQuestion) {
      const result = await emberSwal.fire({
        text: `Vous avez complété votre questionnaire. Voulez-vous sauvegarder ?`,
        icon: "question",
        cancelButtonText: "Annuler",
        showCancelButton: true,
        confirmButtonText: "Sauvegarder",
        confirmButtonColor: "#d33"
      });

      if (result.isConfirmed) {
        await this.saveAndTransition();
      }
    } else {
      this.nextQuestion();
    }
  }

  @action
  async saveAndTransition(): Promise<void> {
    await this.save();
    if (this.afterCompleted) {
      await this.afterCompleted();
    }
  }
}
